import { memo, useMemo } from "react"

import type { ReportingQueryProps } from "../reporting/reporting-query"
import type {
	ReportingTooltipEntry,
	ReportingTooltipTable,
} from "@/components/reporting/charts"
import {
	formatReportingOrigin,
	ReportingBarChart,
} from "@/components/reporting/charts"
import { api } from "@/lib/api"
import { channelResolutionRate, formatResolutionRate } from "./resolution-rate"

const TOOLTIP_TABLE: ReportingTooltipTable = {
	headers: ["Channel", "Resolved", "Resolution rate"],
	metric: (entry: ReportingTooltipEntry) => {
		const rate = channelResolutionRate(entry)
		return rate === null ? null : formatResolutionRate(rate)
	},
}

// Shared by the AI reporting tab and the Agent page, so both read the same history.
export const ResolutionsByChannelChart = memo(
	({
		query,
		height,
		flush = false,
		showRate = true,
		className,
	}: {
		query: ReportingQueryProps
		height: number
		flush?: boolean
		// Off where a card beside the chart already shows the rate.
		showRate?: boolean
		className?: string
	}) => {
		const resolutions =
			api.workspace.getReportingAgentResolutionsByOrigin.useQuery(query, {
				cacheTime: Infinity,
				staleTime: 60_000,
			})
		const series = useMemo(
			() =>
				(resolutions.data?.origins ?? []).map((origin, index) => ({
					key: origin,
					label: formatReportingOrigin(origin),
					tone: `categorical-${index}` as const,
				})),
			[resolutions.data?.origins],
		)
		const sessions = resolutions.data?.sessions ?? 0
		const resolutionRate = resolutions.data?.resolutionRate ?? 0
		const resolved = resolutions.data?.resolved ?? 0
		// Axes around nothing read as a broken chart; the rate card already says there is no data.
		if (!resolutions.isLoading && sessions === 0) return null
		return (
			<ReportingBarChart
				title="Resolutions by channel"
				description="Conversations the Productlane Agent resolved without your team replying, stacked by the channel they came in on. The rate is the share of the agent's conversations it resolved."
				data={resolutions.data?.points ?? []}
				series={series}
				isLoading={resolutions.isLoading}
				legend={
					sessions === 0 ? undefined : showRate ? (
						<span className="text-xs text-tertiary">
							Resolution rate{" "}
							<span className="font-medium text-primary">
								{formatResolutionRate(resolutionRate)}
							</span>
						</span>
					) : (
						// The rate sits in the card beside it; the total is what this chart adds.
						<span
							data-testid="agent-resolutions-total"
							className="text-xs text-tertiary"
						>
							<span className="font-medium tabular-nums text-primary">
								{resolved}
							</span>{" "}
							resolved
						</span>
					)
				}
				tooltipTable={TOOLTIP_TABLE}
				compactTooltip
				stacked
				flush={flush}
				height={height}
				className={className}
			/>
		)
	},
)
